import Theme from "../../Theme"
import { OPTIMISATION_ACTIVITY_STATUS } from "../../models/EditorModel"
import { AlertsByClassification, AlertsByClassificationInMonth, AlertsBySeverity, AlertsBySeverityInMonth, AlertsBySourceInMonth, Counts, OptimisationActivity, ReportObject, WorkspaceCostAnalysis } from "../../models/ReportModel"
import { ReportCardBGStyle, ReportCardTileStyle, ReportPageStyle, TextInCardStyle } from "../../models/StyleModel"
import { setCurrentReport } from "../../redux/dataSlice"
import { useAppSelector } from "../../redux/hook"
import { getFromPath, sectionNameToUpperCase } from "../../service/dataLogic"
import { PieChartData } from "../report/PieChartColorFromData"
import PieChartForSLA from "../report/PieChartForSLA"
import PieChartNoLegends from "../report/PieChartNoLegends"
import DefendLogo from "../utility/DefendLogo"
import TrendMark from "../utility/TrendMark"

const titleTextColor = "text-neutral-50"

const IMMonthlyReport = ({showHeader = false, page = -1, bgColor = "bg-neutral-50", renderPage = false}) => {
    const currentReport = useAppSelector((state) => state.data.currentReport)
        
    if (!currentReport) {
        return (
            <div className={`${ReportPageStyle} w-full h-60 flex justify-center items-center`}>
                <h1 className={`text-white text-xl font-medium w-fit px-6 py-1.5`}>No Report Selected</h1>
            </div>
        )
    }
    
    const incidentsOverviewContent = getFromPath(currentReport, "content.events.incidents.overview") as string[]
    const nearMissesOverviewContent = getFromPath(currentReport, "content.events.near_misses.overview") as string[]
    
    const incidentsOverviewCount = incidentsOverviewContent ? incidentsOverviewContent.reduce((acc, item) => acc + item.length, 0) : 0
    const nearMissesOverviewCount = nearMissesOverviewContent ? nearMissesOverviewContent.reduce((acc, item) => acc + item.length, 0) : 0

    // If one of these is over 500 characters and the other is below 300, we can shrink the lower.
    const shouldShrinkNearMissesOverview = incidentsOverviewCount > 500 && nearMissesOverviewCount < 300
    const shouldShrinkIncidentsOverview = nearMissesOverviewCount > 500 && incidentsOverviewCount < 300

    let incidentsCols = showHeader ? 3 : 4
    let nearMissesCols = showHeader ? 3 : 4

    if (shouldShrinkIncidentsOverview) {
        incidentsCols -= 1
        nearMissesCols += 1
    }

    if (shouldShrinkNearMissesOverview) {
        nearMissesCols -= 1
        incidentsCols += 1
    }

    const incidentColString = `col-span-${incidentsCols}`
    const nearMissesColString = `col-span-${nearMissesCols}`

    if (page == 0){
        return renderPageOne(currentReport, showHeader, bgColor, renderPage, incidentColString, nearMissesColString);
    }

    if (page == 1){
        return renderPageTwo(currentReport, showHeader, bgColor);
    }
    
    return (
        <div>            
            <div id="ReportContainer" className={`${bgColor} w-full aspect-w-16 grid grid-cols-12 gap-2 p-2`}>                                
                {showHeader && (
                    <div className="col-span-2 relative h-full flex flex-col justify-center items-start">
                        <div className="pl-2">
                            <DefendLogo />
                            <h2 className="text-neutral-900 overline decoration-neutral-900">SECURITY OPERATIONS DASHBOARD</h2>
                        </div>
                        {renderHeader(currentReport)}
                    </div>
                )}
                {/*Incidents*/}
                <div className={`col-span-2 ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Incidents</h1>
                    {renderCount(currentReport, "content.events.incidents.counts")}
                </div>  
                {/*Incidents overview*/}
                <div className={`${incidentColString} ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Incidents Overview</h1>
                    {renderStringArray(currentReport, "content.events.incidents.overview")}
                </div>                      
                {/*Near misses*/}
                <div className={`col-span-2 ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Near Misses</h1>
                    {renderCount(currentReport, "content.events.near_misses.counts")}
                </div>            
                {/*Near miss overview*/}
                <div className={`${nearMissesColString} ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Near Miss Overview</h1>
                    {renderStringArray(currentReport, "content.events.near_misses.overview")}
                </div>  

                {/* ROW 2 */}
                {/*SLA*/}
                <div className={`col-span-2 ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>SLA: Time To First Response</h1>
                    {renderSLATicketsCount(currentReport, renderPage)}
                </div>             
                {/*Alert report*/}
                <div className={`col-span-2 ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle} pr-12`}>Alert Report</h1>
                    {renderAlertsBySeverity(currentReport)}
                </div>            
                {/*Alert classification*/}
                <div className={`col-span-4 ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} pr-12 bg-ui-report-title-background-green bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Alert Classification</h1>
                    {renderAlertsByClassification(currentReport, renderPage)}
                </div>            
                {/*MS Alert sources*/}
                <div className={`col-span-4 ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} bg-ui-report-title-background-green bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Alert Sources</h1>
                    {renderAlertsBySource(currentReport)}
                </div>  
                {/*Activity Summary*/}
                {currentReport && (getFromPath(currentReport, "content.general") as unknown as Object).hasOwnProperty("total_alerts") && 
                    <div className={`col-span-2 ${ReportCardBGStyle} relative`}>
                        <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Activity Summary</h1>
                        {renderActivitySummary(currentReport)}
                    </div>                
                }
                <div className={`${currentReport && (getFromPath(currentReport, "content.general") as unknown as Object).hasOwnProperty("total_alerts") ?
                    "col-span-2" : "col-span-3"} relative flex flex-col gap-2`}>
                    {/* ROW 3 */}
                    {/*Recommendations*/}
                    <div className={`grow ${ReportCardBGStyle} col-span-2 relative`}>
                        <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Recommendations</h1>
                        {renderStringArray(currentReport, "content.general.recommendations")}
                    </div>  
                    {/*Cost Analysis*/}          
                    <div className={`grow ${ReportCardBGStyle} relative`}>
                        <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Cost Analysis (Estimated)</h1>
                        {renderCostAnalysis(currentReport)}
                    </div>                  
                </div>
                
            
                {/*Optimisation activity*/}
                <div className={`${currentReport && (getFromPath(currentReport, "content.general") as unknown as Object).hasOwnProperty("total_alerts") ?
                    "col-span-4":"col-span-5"} ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Optimisation Activity</h1>
                    {renderOptimisationActivity(currentReport)}
                </div>

                {/*Summary*/}
                <div className={`col-span-4 ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Summary</h1>
                    {renderSummary(currentReport)}
                </div>   
            </div>
        </div>
    )
}

const renderPageOne = (currentReport: ReportObject, showHeader: boolean, bgColor: string, renderPage: boolean, incidentColString: string, nearMissesColString: string) => {
    return (
        <div>
            <div id="ReportContainer" className={`p-3 ${bgColor} w-full aspect-w-16 grid grid-cols-12 gap-2 p-2`}>                
                {showHeader && (
                    <div className="col-span-2 relative h-full flex flex-col justify-center items-start">
                        <div className="pl-2">
                            <DefendLogo />
                            <h2 className="text-neutral-900 overline decoration-neutral-900">SECURITY OPERATIONS DASHBOARD</h2>
                        </div>
                        {renderHeader(currentReport)}
                    </div>
                )}
                {/*Incidents*/}
                <div className={`col-span-2 ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Incidents</h1>
                    {renderCount(currentReport, "content.events.incidents.counts")}
                </div>  
                {/*Incidents overview*/}
                <div className={`${incidentColString} ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Incidents Overview</h1>
                    {renderStringArray(currentReport, "content.events.incidents.overview")}
                </div>                      
                {/*Near misses*/}
                <div className={`col-span-2 ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Near Misses</h1>
                    {renderCount(currentReport, "content.events.near_misses.counts")}
                </div>            
                {/*Near miss overview*/}
                <div className={`${nearMissesColString} ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Near Miss Overview</h1>
                    {renderStringArray(currentReport, "content.events.near_misses.overview")}
                </div>  

                {/* ROW 2 */}
                {/*SLA*/}
                <div className={`col-span-2 ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>SLA: Time To First Response</h1>
                    {renderSLATicketsCount(currentReport, renderPage)}
                </div>             
                {/*Alert report*/}
                <div className={`col-span-2 ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle} pr-12`}>Alert Report</h1>
                    {renderAlertsBySeverity(currentReport)}
                </div>            
                {/*Alert classification*/}
                <div className={`col-span-4 ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} pr-12 bg-ui-report-title-background-green bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Alert Classification</h1>
                    {renderAlertsByClassification(currentReport, renderPage)}
                </div>            
                {/*MS Alert sources*/}
                <div className={`col-span-4 ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} bg-ui-report-title-background-green bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Alert Sources</h1>
                    {renderAlertsBySource(currentReport)}
                </div>  
    
                <div className={`${currentReport && (getFromPath(currentReport, "content.general") as unknown as Object).hasOwnProperty("total_alerts") ?
                    "col-span-4" : "col-span-4"} relative flex flex-col gap-2`}>
                    {/* ROW 3 */}
                    {/*Activity Summary*/}
                    {currentReport && (getFromPath(currentReport, "content.general") as unknown as Object).hasOwnProperty("total_alerts") && 
                        <div className={`grow ${ReportCardBGStyle} relative`}>
                            <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Activity Summary</h1>
                            {renderActivitySummary(currentReport)}
                        </div>                
                    }                 
                </div>
                <div className={`${currentReport && (getFromPath(currentReport, "content.general") as unknown as Object).hasOwnProperty("total_alerts") ?
                    "col-span-8" : "col-span-8"} relative flex flex-col gap-2`}>
                    {/*Cost Analysis*/}          
                    <div className={`grow ${ReportCardBGStyle} relative`}>
                        <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Cost Analysis (Estimated)</h1>
                        {renderCostAnalysis(currentReport, true)}
                    </div>                  
                </div>
            </div>
        </div>
    )
}

const renderPageTwo = (currentReport: ReportObject, showHeader: boolean, bgColor: string) => {
    return (
        <div>
            <div id="ReportContainer" className={`p-3 ${bgColor} w-full aspect-w-16 grid grid-cols-12 gap-2 min-h-96 p-2`}>                
                {/*Recommendations*/}
                <div className={`${ReportCardBGStyle} col-span-5 relative`}>
                    <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Recommendations</h1>
                    <div className="pl-4">
                        {renderStringArray(currentReport, "content.general.recommendations", true)}
                    </div>
                </div>  

                {/*Summary*/}
                <div className={`col-span-7 row-span-2 ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Summary</h1>
                    {renderSummary(currentReport, true)}
                </div>   
            
                {/*Optimisation activity*/}
                <div className={`${currentReport && (getFromPath(currentReport, "content.general") as unknown as Object).hasOwnProperty("total_alerts") ?
                    "col-span-5":"col-span-5"} ${ReportCardBGStyle} relative`}>
                    <h1 className={`${titleTextColor} bg-ui-report-title-background-green ${ReportCardTileStyle}`}>Optimisation Activity</h1>
                    {renderOptimisationActivity(currentReport)}
                </div>
            </div>
        </div>
    )
}

const renderHeader = (currentReport: ReportObject) => {
    if (currentReport === null) return 
    const company = getFromPath(currentReport, "content.company_name") as string
    const startDate = getFromPath(currentReport, "content.start_date") as string 
    const date = startDate.slice(startDate.indexOf(" ")+1)

    return (
        <div className="h-full pr-2 pt-2 px-2 mt-2 mb-1 border-[2px] border-center rounded-md border-gray-400">
            <div className="flex flex-row justify-between">
                <div className="py-1 flex flex-col gap-2">
                    <h1 className="text-neutral-900">MONTHLY REPORT:</h1>
                    <h1 className="text-neutral-900">{company}</h1>
                </div>
                <h1 className={`py-1 ${TextInCardStyle}`}>{date}</h1>
            </div>
            <div>
                <h3 className="py-1 text-xs text-neutral-900">Incident Management, Handling and Response</h3>
            </div>
        </div>
    )
}

const renderCount = (currentReport: ReportObject, path:string) => {
    if (currentReport === null) return 
    const content = getFromPath(currentReport, path) as unknown as Counts
    const trend = content.current > content.previous ? "up" : content.current === content.previous ? "equal" : "down"

    return (
        <div className="m-2 grid grid-rows-4">
            <div className={`row-span-3 text-neutral-900 py-8 text-center text-6xl`}>{content.current}</div>
            <div className={`row-span-1 place-self-center`}>
                <TrendMark trend={trend} difference={0} /> 
            </div>
        </div>
    )
}

const renderStringArray = (currentReport: ReportObject, path:string, multipage: boolean = false) => {
    if (currentReport === null) return 
    const content = getFromPath(currentReport, path) as unknown as string[]

    if (path.includes("recommendations")) {
        return (
            <ul className="list-disc ml-7">
                {content.map((item,index) => (
                    <li key={index} className={`${multipage ? 'text-m' : 'text-xs'} ${TextInCardStyle} pb-2`}>{item}</li>
                ))}
            </ul>
        )
    } else {
        return (
            <div className={`m-2 py-2`}>
                {content.map((item,index) => (
                    <div key={index} className={`${multipage ? 'text-m' : 'text-xs'} ${TextInCardStyle} whitespace-pre-wrap`}>{item}</div>
                ))}
            </div>
        )            
    }
}

const renderSummary = (currentReport: ReportObject, multipage: boolean = false) => {
    if (currentReport === null) return
    const content = getFromPath(currentReport, "content.general.summary") as string
    return (
        <div className={`m-2 ${multipage ? 'text-m' : 'text-xs'} ${TextInCardStyle} whitespace-pre-wrap`}>{content}</div>
    )
}

const renderAlertsBySeverity = (currentReport: ReportObject) => {
    if (currentReport === null) return
    const content = getFromPath(currentReport, "content.alerts.by_severity") as unknown as AlertsBySeverityInMonth
    const currentContent = content.current
    const previousContent = content.previous

    return (
        <div className="h-[80%] flex flex-col">
            {Object.entries(currentContent).map(([key,value]) => {
                let color = "bg-gray-500"
                if (key === "high") {color = "bg-ui-report-high-severity"}
                else if (key === "medium") {color = "bg-ui-report-medium-severity"}
                else if (key === "low") {color = "bg-ui-report-low-severity"}

                const trend = value>previousContent[key as keyof AlertsBySeverity] ? "up" : value===previousContent[key as keyof AlertsBySeverity] ? "equal":"down"
                return (
                <div className="py-2 grid grid-cols-6 place-items-center" key={key}>
                    <div className={`col-span-1 ps-2 text-medium place-self-end pt-1 ${TextInCardStyle}`}>{value}</div>
                    <div className={`col-span-4 justify-self-start flex flex-row pl-1`}>
                        <div className={`${color} ml-1 w-1 h-6 place-self-center`} />
                        <div className={`pl-2 text-sm place-self-end ${TextInCardStyle}`}>{key.charAt(0).toUpperCase()+key.slice(1)}</div>                            
                    </div>
                    <div className={`col-span-1 pr-4`}><TrendMark trend={trend} difference={0} /></div>
                </div>
            )})}
        </div>
    )
}

const renderAlertsByClassification = (currentReport: ReportObject, renderPage: boolean) => {
    if (currentReport === null) return 
    const content = getFromPath(currentReport, "content.alerts.by_classification") as unknown as AlertsByClassificationInMonth
    const currentContent = content.current
    const previousContent = content.previous
    
    const chartArr: PieChartData[] = []
    let chartColor:string = "" 
    for (let key in currentContent) {
        if (key === "undetermined") continue
        else if (key === "benignpositive") {chartColor = "rgb(74 222 128)"}
        else if (key === "falsepositive") {chartColor = "rgb(255 179 67)"}
        else if (key === "truepositive") { chartColor = "rgb(255 113 113)"}
        chartArr.push({id:key,label:key,value:currentContent[key as keyof AlertsByClassification],color:chartColor})
    }

    const width = renderPage ? 170 : undefined
    const height = renderPage ? 170 : undefined

    return (
        <div className="grid grid-cols-5 h-[80%]">
            <div className="col-span-2 flex flex-col justify-center items-center pl-2">
                <PieChartNoLegends data={chartArr} dataType="Alerts" centerText="Alerts" width={width} height={height} />                    
            </div>
            <div className="col-span-3 grid content-center">
                {Object.entries(currentContent).filter(([key, _]) => key !== "undetermined")
                    .map(([key,value]) => {
                    
                    let color:string = ""
                    let text:string = ""
                    if (key === "truepositive") { color = "bg-ui-report-high-severity"; text = "True Positive"}
                    else if (key === "benignpositive") {color = "bg-ui-report-low-severity"; text = "Benign Positive"}
                    else if (key === "falsepositive") {color = "bg-ui-report-medium-severity"; text = "False Positive"}

                    const trend = value > previousContent[key as keyof AlertsByClassification] ? "up" : value===previousContent[key as keyof AlertsByClassification] ? "equal" : "down"
                    return (
                        <div className="grid grid-cols-6 place-items-center" key={key}>
                            <div className={`col-span-5 py-4 place-self-start flex flex-row`}>
                                <div className={`col-span-1 text-medium text-end place-self-center min-w-[30px] ${TextInCardStyle}`}>{value}</div>
                                <div className={`${color} w-1 h-5 mb-0.5 ml-2 place-self-center`} />
                                <div className={`pl-2 text-sm place-self-center ${TextInCardStyle}`}>{text}</div>                                    
                            </div>
                            <div className={`col-span-1 pe-4`}><TrendMark trend={trend} difference={0} /></div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

const renderAlertsBySource = (currentReport: ReportObject) => {
    if (currentReport === null) return 
    const content = getFromPath(currentReport, "content.alerts.by_source") as unknown as AlertsBySourceInMonth
    const currentContent = content.current
    const previousContent = content.previous

    return (
        <div className="flex flex-col pr-4 pb-4 h-[80%]">
            {/* Data Rows */}
            {currentContent.length === 0 ? 
                <div className="text-sm font-light px-2">There were no alerts this month</div>
                :
                currentContent.map((item, index) => {
                    const previousItem = previousContent.find(source => source.source === item.source)
                    let previousCount = previousItem ? previousItem.count : 0
                    const trend = item.count > previousCount ? "up" : item.count === previousCount ? "equal" : "down"                    
                    const trendColor = item.count > previousCount ? "bg-ui-report-high-severity": "bg-ui-report-low-severity"
                    return (
                        <div className="flex flex-row items-center py-1" key={index}>
                            <div className="flex-1 flex flex-row border-b border-gray-200 items-center ml-2">
                                <div className="w-8 text-right font-light text-sm text-neutral-900 pr-2">{item.count}</div>
                                    <div className={`${trendColor} w-1 h-4`} />
                                    <div className="pl-2 font-light text-sm text-neutral-900">{item.source}</div>
                            </div>
                            <div className="w-12 flex justify-center">
                                <TrendMark trend={trend} difference={0} />
                            </div>
                        </div>
                    )
                })}
        </div>
    )
}

const renderOptimisationActivity = (currentReport: ReportObject) => {
    if (currentReport === null) return 
    const content = getFromPath(currentReport, "content.general.optimisation_activity") as unknown as OptimisationActivity[]
    let sorted = [...content].sort((a,b) => b.status - a.status)

    const padToColumns = 8;

    return (
        <div className="w-full h-[80%] flex justify-between pb-2 px-2">
            {sorted.length === 0 ?
                <div className="text-sm font-light px-2">There were no optimisation activities this month</div>
                :
                <table className="table-auto w-full">
                    <thead className="bg-gray-50 border-t-[2px] border-b-[2px] border-ui-report-border-green">
                        <tr>
                            <th className="rounded-sm text-sm font-medium text-left pl-2 py-1">Activity</th>
                            <th className="rounded-sm text-sm font-medium py-1 text-end pr-4">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((activity, rowIndex) => {
                            let rowColorTop:string = ""
                            let rowColorMid:string = ""
                            let rowColorBot:string = ""
                            if (rowIndex % 2 === 0) {
                                rowColorTop = "bg-ui-report-table-bg-green"
                                rowColorMid = "bg-ui-report-table-bg-green-light"
                                rowColorBot = "bg-ui-report-table-bg-green"
                            }
                            else {
                                rowColorTop = "bg-slate-200/20"
                                rowColorMid = "bg-slate-100"
                                rowColorBot = "bg-neutral-200/20"
                            }
                            // 1: "Complete",
                            // 2: "On-Hold",
                            // 3: "In Progress",
                            // 4: "Scheduled",
                            // 5: "Not Started"
                            let statusColor:string = ""
                            let statusText:string = ""
                            if (activity.status === 1) {statusColor = "text-ui-report-low-severity"; statusText = OPTIMISATION_ACTIVITY_STATUS[activity.status]}
                            else if (activity.status === 2) {statusColor = "text-purple-500"; statusText = OPTIMISATION_ACTIVITY_STATUS[activity.status]}
                            else if (activity.status === 3) {statusColor = "text-ui-report-medium-severity"; statusText = OPTIMISATION_ACTIVITY_STATUS[activity.status]}
                            else if (activity.status === 4) {statusColor = "text-ui-report-medium-severity"; statusText = OPTIMISATION_ACTIVITY_STATUS[activity.status]}
                            else if (activity.status === 5) {statusText = OPTIMISATION_ACTIVITY_STATUS[activity.status]}
                            return (
                                <>
                                <tr className={`${rowColorTop}`} key={`top-${rowIndex}`}>
                                    <td className={`box-border rounded-sm text-xs font-medium pl-1 pt-2 text-neutral-900 whitespace-nowrap`}>
                                    </td>
                                     <td className={`box-border rounded-sm text-xs text-center font-medium whitespace-nowrap pt-2 pr-2`}>
                                    </td>
                                </tr>
                                <tr className={`${rowColorMid}`} key={`mid-${rowIndex}`}>
                                    <td className={`box-border rounded-sm text-xs font-medium pl-1 text-neutral-900 pt-1`}>
                                        <div className="pl-1">
                                            {activity.name}
                                        </div>
                                    </td>
                                    <td className={`text-end box-border rounded-sm text-xs font-medium whitespace-nowrap pr-2 pt-1 ${statusColor}`}>
                                        <div className="pl-1">
                                            {statusText}
                                        </div>
                                    </td>
                                </tr>
                                <tr className={`${rowColorBot}`} key={`bottom-${rowIndex}`}>
                                    <td className={`box-border rounded-sm text-xs font-medium pl-1 pt-2 text-neutral-900 whitespace-nowrap`}>
                                    </td>
                                     <td className={`box-border rounded-sm text-xs text-center font-medium whitespace-nowrap pt-2 pr-2`}>
                                    </td>
                                </tr>
                                </>
                            )
                        })}
                        {sorted.length < padToColumns && 
                            Array.from({ length: padToColumns - sorted.length }, (_, i) => (
                                <tr className={`bg-gray-50 h-6 min-h-6`} key={`empty-row-${i}`}>
                                    <td className="rounded-sm text-xs pl-1 py-1"></td>
                                    <td className="rounded-sm text-xs text-center font-medium whitespace-nowrap py-1"></td>
                                </tr>
                            ))
                        }
                    </tbody>
                </table>                     
            }
        </div>
    )
}

const renderSLATicketsCount = (currentReport: ReportObject, renderPage: boolean) => {
    if (currentReport === null) return 
    const totalCount = getFromPath(currentReport, "content.general.total_ticket") as number
    const slaCount = getFromPath(currentReport, "content.general.sla_breach_ticket") as number
    const nonSlaCount : number = totalCount - slaCount
    const nonSlaPercent = totalCount === 0 ? "100" : ((nonSlaCount * 1.0 / totalCount) * 100).toFixed(0)
    const chartData : PieChartData[] = [
        {
            id: "SLA",
            label: "SLA",
            value: slaCount,
            color: "rgb(255 113 113)"
        },
        {
            id: `${nonSlaPercent}%`,
            label: `${nonSlaPercent}%`,
            value: nonSlaCount,
            color: "rgb(74 222 128)"
        }
    ]

    const summary = slaCount === 0 ? 
        "There were no breaches of time to first response SLAs this month."
        :
        `There ${slaCount > 1 ? "were" : "was"} ${slaCount} ${slaCount > 1 ? "breaches" : "breach"} of time to first response SLAs this month`

    const width = renderPage ? 100 : undefined
    const height = renderPage ? 100 : undefined

    return (
        <div className="w-full h-[80%] px-2 text-center flex flex-col">
            <PieChartForSLA data={chartData} width={width} height={height} />
            <div className="text-xs font-light h-full text-center content-center pb-4">{summary}</div>
        </div>
    )
}

const renderCostAnalysis = (currentReport: ReportObject, multipage = false) => {
    if (currentReport === null) return
    const content = getFromPath(currentReport, "content.workspace_cost_analysis") as unknown as WorkspaceCostAnalysis
    if (!content) return

    if (multipage) {
        return (
                <div className={`flex flex-col px-4 pb-4 gap-2`}>
                    {/* Headers */}
                    <div className="flex flex-row border-b-2 border-ui-report-border-green pb-2">
                    <div className="flex-1 font-medium text-sm text-neutral-900">Workspace</div>
                    {Object.values(content)[0]?.data_ingestion !== undefined && (
                        <div className="w-20 text-right font-medium text-sm text-neutral-900">Data (GB)</div>
                    )}
                    <div className="w-24 text-right font-medium text-sm text-neutral-900">Total Cost</div>
                    <div className="w-24 text-right font-medium text-sm text-neutral-900">Avg Cost</div>
                    <div className="w-12 text-center font-medium text-sm text-neutral-900"></div>
                    </div>
                    
                    {/* Data Rows */}
                    {Object.entries(content).map(([key, value]) => {
                    const text = sectionNameToUpperCase(value.workspace_name);
                    const totalCost = value.total_cost.toFixed(2);
                    const avgCost = value.avg_cost.toFixed(2);
                    const preCost1 = value.pre_total_cost
                    const preCost2 = value.cost_two_months_ago
                    const preCost3 = value.cost_three_months_ago

                    const costTrend = preCost1 === 0 ? 
                        "equal" : value.total_cost > preCost1 ? 
                        "up" : value.total_cost === preCost1 ? 
                            "equal" : "down"

                    let maxDiff = 0

                    if (costTrend === "up" && preCost1 !== 0) {
                        maxDiff = [preCost1, preCost2, preCost3].reduce((accumulator, current) => {
                            if (current === 0) {
                            return accumulator
                            } else {
                            return Math.max(accumulator, (value.total_cost / current - 1))
                            }
                        }, 0)
                    }
                    
                    return (
                        <div className="flex flex-row items-center py-2 border-b border-gray-200" key={key}>
                        <div className="flex-1 font-light text-sm text-neutral-900 overflow-hidden">{text}</div>
                        {value.data_ingestion !== undefined && (
                            <div className="w-20 text-right font-light text-sm text-neutral-900">{value.data_ingestion.toFixed(2)}</div>
                        )}
                        <div className="w-24 text-right font-light text-sm text-neutral-900">${totalCost}</div>
                        <div className="w-24 text-right font-light text-sm text-neutral-900">${avgCost}</div>
                        <div className="w-12 flex justify-center">
                            <TrendMark trend={costTrend} difference={maxDiff} />
                        </div>
                        </div>
                    );
                    })}
                </div>
            )
    } else {
        return (
            <div className="flex flex-col px-4 pb-4 gap-2 text-sm text-neutral-900">
                {Object.entries(content).map(([key, value], index, array) => {
                const text = sectionNameToUpperCase(value.workspace_name);
                const totalCost = value.total_cost.toFixed(2);
                const avgCost = value.avg_cost.toFixed(2);
                const preCost1 = value.pre_total_cost;
                const preCost2 = value.cost_two_months_ago;
                const preCost3 = value.cost_three_months_ago;

                const costTrend =
                    preCost1 === 0
                    ? "equal"
                    : value.total_cost > preCost1
                    ? "up"
                    : value.total_cost === preCost1
                    ? "equal"
                    : "down";

                let maxDiff = 0;

                if (costTrend === "up" && preCost1 !== 0) {
                    maxDiff = [preCost1, preCost2, preCost3].reduce((acc, current) => {
                    if (current === 0) {
                        return acc;
                    } else {
                        return Math.max(acc, value.total_cost / current - 1);
                    }
                    }, 0);
                }

                return (
                    <div
                    key={key}
                    className={`flex flex-col gap-1 pb-2 ${
                        index !== array.length - 1 ? "border-b border-gray-200" : ""
                    }`}
                    >
                    <div className="font-medium">{text}</div>

                    {value.data_ingestion !== undefined && (
                    <div className="flex justify-between items-center">
                        <span>Data:</span>
                        <span className="flex items-center justify-end gap-1 text-right">
                            <span>{value.data_ingestion.toFixed(2)} GB</span>
                            <span className="pl-2">
                                <TrendMark trend={costTrend} difference={maxDiff} />
                            </span>                        
                        </span>
                        </div>
                    )}

                    <div className="flex justify-between items-center">
                        <span>Total:</span>
                        <span className="flex items-center justify-end gap-1 text-right">
                            <span>${totalCost}</span>
                            <span className="pl-2">
                                <TrendMark trend={costTrend} difference={maxDiff} />
                            </span>
                        </span>
                    </div>

                    <div className="flex justify-between items-center">
                        <span>Average:</span>
                        <span className="flex items-center justify-end gap-1 text-right">
                            <span>${avgCost}</span>
                            <span className="pl-2">
                               <div className={`w-fit border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[10px] border-white`} />
                            </span>
                        </span>
                    </div>
                    </div>
                );
                })}
            </div>
            );
    }
}

const renderActivitySummary = (currentReport: ReportObject) => {
    if (currentReport === null) return 
    const totalTickets = getFromPath(currentReport, "content.general.total_ticket") as unknown as number
    const pendingTickets = getFromPath(currentReport, "content.general.pending_tickets") as unknown as number
    const totalAlerts = getFromPath(currentReport, "content.general.total_alerts") as unknown as number
    const pendingAlerts = getFromPath(currentReport, "content.general.pending_alerts") as unknown as number

    if (totalAlerts === undefined) {
        return 
    }

    const activitySummary = {
        "Total Alerts": totalAlerts,
        "Pending Alerts": pendingAlerts,
        "Total Tickets": totalTickets,
        "Pending Tickets": pendingTickets
    }

    return (
        <div className="h-[90%] flex flex-col px-4 gap-2">
            {Object.entries(activitySummary).map(([key, value]) => {
                return(
                    <div className="flex flex-row justify-between border-b-[1px] border-ui-report-border-green" key={key}>
                        <div className={`font-light text-sm text-neutral-900 `}>{key}</div>
                        <div className={`font-light text-sm text-neutral-900 `}>{value}</div>
                    </div>
                )
            })}
        </div>
    )
}

export default IMMonthlyReport